/* istanbul ignore file */
import ShowbieAuthenticator from './showbie';
import { v4 as uuidv4 } from 'uuid';
import { service } from '@ember/service';
import HttpAnalyticsService from 'dashboard/services/http-analytics';
import SessionService from 'dashboard/services/-session';
import { resolveRegion } from '@showbie/authentication';
import type { RegionResolutionData } from '@showbie/authentication';
import type {
  ShowbieOAuthRequestBody,
  ShowbieSessionData,
  UserOAuthCredentials,
} from 'dashboard/types';
import { ShowbieServiceError } from 'dashboard/utils/errors';
import ENV from 'dashboard/config/environment';
import { waitForFetch } from '@ember/test-waiters';

const { auth } = ENV.apiConfig.services;

export default class Oauth2Authenticator extends ShowbieAuthenticator {
  @service declare httpAnalytics: HttpAnalyticsService;
  @service declare session: SessionService;

  async authenticate({
    accessToken,
    provider,
    idToken,
  }: UserOAuthCredentials): Promise<ShowbieSessionData> {
    const fingerprint = uuidv4();
    const headers = this.httpAnalytics.headers;

    const regionData: RegionResolutionData = {
      contextType: 'sso',
      context: accessToken,
      provider,
    };
    const opts = { headers };

    // resolve and store the regional origin
    const region = await resolveRegion(regionData, opts, auth.origin);
    this.session.setApiRegion(region);

    const requestBody: ShowbieOAuthRequestBody = {
      grant_type: 'access_token',
      intent: 'get',
      assertion: accessToken,
      provider,
      fingerprint,
    };

    if (idToken) {
      requestBody.id_token = idToken;
    }

    const resp: Response = await waitForFetch(
      fetch(this.session.buildUrl('/sessions/oauth'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify(requestBody),
      }),
    );

    const data = await resp.json();

    if (!resp.ok) {
      throw new ShowbieServiceError(data.errors);
    }

    return {
      ...data.session,
      region,
      fingerprint,
    };
  }
}
